import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import {ReactNode, createContext, useMemo, useState, useEffect} from "react";
import lightLogo from "../../../public/AI4Knowledge-logo-color.png";
import darkLogo from "../../../public/AI4Knowledge-logo-white.png";
import lightBg from "../../../public/bgLight.svg";
import darkBg from "../../../public/bgDark.svg";

type ThemeContextType = {
  switchColorMode: () => void;
};

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeContext = createContext<ThemeContextType>({
  switchColorMode: () => {},
});

declare module '@mui/material/styles' {
  interface Theme {
    logo: string;
  }
  interface ThemeOptions {
    logo?: string;
  }
}
declare module "@mui/material/styles/createPalette" {
  interface Palette {
    customPaper?: string;
    gradientBg?: string;
    primary_tone_1?: string;
    primary_tone_2?: string;
    primary_tone_3?: string;
    tetriary?: string;
  }
  interface PaletteOptions {
    customPaper?: string;
    gradientBg?: string;
    primary_tone_1?: string;
    primary_tone_2?: string;
    primary_tone_3?: string;
    tetriary?: string;
  }
}

export function ThemeContextProvider({children}: ThemeProviderProps) {
  const [mode, setMode] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedMode = localStorage.getItem("theme-mode") as "light" | "dark" | null;
    if(storedMode === "light" || storedMode === "dark") {
      setMode(storedMode);
    }
    setMounted(true);
  }, []);

  const switchColorMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("theme-mode", newMode);
      return newMode;
    });
  };

  const primaryLight = "rgb(228, 238, 249)";
  const primaryDark = "rgb(4, 32, 57)";
  const secondaryLight = "rgb(252, 252, 252)";
  const secondaryDark = "rgb(12, 58, 107)";
  const active = "rgb(93, 185, 252)";
  const primaryDark70 = "rgba(4, 32, 57, 0.7)86.90%, 12.00%, 0.70)";
  const primaryLight70 = "rgba(228, 238, 249, 0.7)";


  const theme = useMemo(() => {
    const baseTheme = createTheme({
      palette: {
        mode,
        ...(mode === "light"
          ? {
            common: {
              black: "rgba(0, 0, 0, 1)",
              white: "rgba(255, 255, 255, 1)",
            },
            primary: {
              main: "rgb(228, 238, 249)",
              light: "rgb(252, 252, 252)",
              contrastText: '#042039',
            },
            secondary: {
              main: "rgb(93, 185, 252)",
              contrastText: "rgba(0, 0, 0, 0.87)",
            },
            background: {
              paper: "rgba(255, 255, 255, 0.97)",
              default: `rgba(228, 238, 249, 0.7)`,
            },
            gradientBg: `url(${lightBg.src})`,
            customPaper: `rgb(211, 228, 253)`,
            primary_tone_1: "rgba(255, 255, 255, 0.21)",
            primary_tone_2: "rgba(211, 228, 253, 1)",
            primary_tone_3: "rgba(227, 242, 253, 1)",
            tetriary: "rgba(24, 23, 23, 0.5)",
          }
          : {
            common: {
              black: "rgba(0, 0, 0, 1)",
              white: "rgba(255, 255, 255, 1)",
            },
            primary: {
              main: "rgb(4, 32, 57)",
              light: "rgb(12, 58, 107)",
              contrastText: "rgb(228, 238, 249)",

            },
            secondary: {
              main: "rgb(93, 185, 252)",
              contrastText: "rgba(0, 0, 0, 0.87)",
            },
            background: {
              default: "rgba(4, 32, 57, 0.7)",
              paper: "rgb(4, 32, 57)",
            },
            text: {
              primary: "rgba(228, 238, 249, 0.87)",
              secondary: "rgba(212, 224, 228, 0.7)",
              disabled: " rgba(4, 32, 57, 0.7)",
            },
            customPaper: "rgba(18, 38, 94, 0.5)",
            gradientBg: `url(${darkBg.src})`,
            primary_tone_1: "rgba(25, 55, 106, 0.28)",
            primary_tone_2: "rgba(17, 31, 72, 1)",
            primary_tone_3: "rgba(13,24,56,1)",
            tetriary: "rgba(255, 255, 255, 1)",
          }),
      },
    });

    return createTheme(baseTheme, {
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              background: `${baseTheme.palette.gradientBg} no-repeat center center fixed`,
              maxHeight: "100vh",
              backgroundSize: "cover",

            },
          },
        },
        MuiTypography: {
          variants: [
            {
              props: {variant: 'bodyBold'},
              style: {
                fontSize: '1rem',
                fontWeight: 700,
              },
            },
          ],
        },
      },
      logo: mode === 'light' ? lightLogo.src : darkLogo.src,
      typography: {
        fontFamily: 'Roboto, Arial, sans-serif',
        fontSize: 14,
        htmlFontSize: 14,
        h5: {
          fontSize: '1.25rem',
        },
        h6: {
          fontSize: '1rem',
        },
        body1: {
          fontSize: '1rem',
        },
        bodyBold: {
          fontSize: '1rem',
        },
        button: {
          fontSize: '1rem',
        },
        subtitle2: {
          fontSize: '0.875rem',
        },
        overline: {
          fontSize: '0.75rem',
        },
      },
    });
  }, [mode]);

  if(!mounted) return null;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeContext.Provider value={{switchColorMode}}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </ThemeContext.Provider>
    </StyledEngineProvider>
  );
}
